if (!customElements.get('mewmora-carousel')) {
  class MewmoraCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.controls = this.querySelector('[data-carousel-controls]');
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      this.status = this.querySelector('[data-carousel-status]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(Number(this.dataset.autoplaySeconds) || 6, 3) * 1000;
      this.currentIndex = 0;

      if (!this.track || this.slides.length === 0) return;

      this.initialized = true;
      this.handleScroll = this.debounce(() => this.updateFromScroll(), 80);
      this.handleResize = this.debounce(() => this.refresh(), 120);
      this.handleVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
      this.handleMotionChange = () => (this.reduceMotion.matches ? this.stopAutoplay() : this.startAutoplay());
      this.handleSectionBlockSelect = (event) => this.selectThemeEditorBlock(event);
      this.handleSectionBlockDeselect = (event) => this.deselectThemeEditorBlock(event);

      this.previousButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1, true));
      this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1, true));
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:block:select', this.handleSectionBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleSectionBlockDeselect);
      window.addEventListener('resize', this.handleResize);
      this.reduceMotion.addEventListener?.('change', this.handleMotionChange);

      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.track);
      this.refresh();
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.stopAutoplay();
      this.track?.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleSectionBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleSectionBlockDeselect);
      window.removeEventListener('resize', this.handleResize);
      this.reduceMotion?.removeEventListener?.('change', this.handleMotionChange);
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    refresh() {
      this.visibleCount = this.getVisibleCount();
      this.maxIndex = Math.max(0, this.slides.length - this.visibleCount);
      this.pageStarts = [];
      for (let index = 0; index < this.slides.length; index += this.visibleCount) {
        this.pageStarts.push(Math.min(index, this.maxIndex));
      }
      this.pageStarts = [...new Set(this.pageStarts)];
      this.currentPage = Math.min(this.currentPage || 0, Math.max(0, this.pageStarts.length - 1));
      this.currentIndex = this.pageStarts[this.currentPage] || 0;
      this.buildDots();
      this.updateControls();
    }

    getVisibleCount() {
      const trackWidth = this.track.clientWidth;
      const slideWidth = this.slides[0]?.getBoundingClientRect().width || trackWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return Math.max(1, Math.min(this.slides.length, Math.round((trackWidth + gap) / (slideWidth + gap))));
    }

    buildDots() {
      if (!this.dotsContainer || !this.controls) return;

      this.dotsContainer.replaceChildren();
      const positions = this.pageStarts.length;
      this.controls.hidden = positions <= 1;

      for (let index = 0; index < positions; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mewmora-stories__dot';
        dot.setAttribute('aria-label', `Show story group ${index + 1} of ${positions}`);
        dot.addEventListener('click', () => this.goToPage(index, true));
        this.dotsContainer.append(dot);
      }
    }

    goTo(index, announce = false) {
      const nextIndex = Math.max(0, Math.min(index, this.maxIndex));
      const target = this.slides[nextIndex];
      if (!target) return;

      this.currentIndex = nextIndex;
      const trackLeft = this.track.getBoundingClientRect().left;
      const targetLeft = target.getBoundingClientRect().left;
      const leadingPadding = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      this.track.scrollTo({
        left: this.track.scrollLeft + targetLeft - trackLeft - leadingPadding,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
      this.updateControls(announce);
      this.restartAutoplay();
    }

    goToPage(page, announce = false) {
      const nextPage = Math.max(0, Math.min(page, this.pageStarts.length - 1));
      this.currentPage = nextPage;
      this.goTo(this.pageStarts[nextPage] || 0, announce);
    }

    updateFromScroll() {
      const leadingPadding = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      const trackLeft = this.track.getBoundingClientRect().left + leadingPadding;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      let closestPage = 0;
      let pageDistance = Number.POSITIVE_INFINITY;
      this.pageStarts.forEach((start, page) => {
        const distance = Math.abs(start - closestIndex);
        if (distance < pageDistance) {
          pageDistance = distance;
          closestPage = page;
        }
      });
      this.currentPage = closestPage;
      this.currentIndex = this.pageStarts[closestPage] || 0;
      this.updateControls();
    }

    updateControls(announce = false) {
      if (this.previousButton) this.previousButton.disabled = this.currentPage === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentPage >= this.pageStarts.length - 1;

      Array.from(this.dotsContainer?.children || []).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.currentPage ? 'true' : 'false');
      });

      this.slides.forEach((slide, index) => {
        const isVisible = index >= this.currentIndex && index < this.currentIndex + this.visibleCount;
        slide.toggleAttribute('inert', !isVisible);
      });

      if (announce && this.status) {
        const end = Math.min(this.currentIndex + this.visibleCount, this.slides.length);
        this.status.textContent = this.visibleCount > 1
          ? `Showing stories ${this.currentIndex + 1} through ${end} of ${this.slides.length}`
          : `Showing story ${this.currentIndex + 1} of ${this.slides.length}`;
      }
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPage(this.currentPage - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToPage(this.currentPage + 1, true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToPage(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToPage(this.pageStarts.length - 1, true);
      }
    }

    selectThemeEditorBlock(event) {
      const slide = this.slides.find((item) => item.dataset.blockId === event.detail.blockId);
      if (!slide || !this.contains(slide)) return;

      this.stopAutoplay();
      this.editorPaused = true;
      const index = this.slides.indexOf(slide);
      this.goToPage(Math.floor(index / this.visibleCount));
    }

    deselectThemeEditorBlock(event) {
      const slide = this.slides.find((item) => item.dataset.blockId === event.detail.blockId);
      if (!slide || !this.contains(slide)) return;

      this.editorPaused = false;
      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.editorPaused || this.reduceMotion.matches || document.hidden || this.matches(':hover') || this.matches(':focus-within') || this.pageStarts.length < 2) return;
      if (this.autoplayTimer) return;

      this.autoplayTimer = window.setInterval(() => {
        const next = this.currentPage >= this.pageStarts.length - 1 ? 0 : this.currentPage + 1;
        this.goToPage(next);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    debounce(callback, delay) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback(...args), delay);
      };
    }
  }

  customElements.define('mewmora-carousel', MewmoraCarousel);
}
